/**
 * Máscaras dinâmicas + validação + rótulos de ocorrência para as seções prefixadas.
 * OBS: o repo do cidadão NÃO possui react-input-mask — usamos formatter manual via onChange.
 * O valor é armazenado FORMATADO no form; validações de comprimento devem usar onlyDigits().
 */

export const onlyDigits = (v?: string | null): string =>
  (v || "").replace(/\D/g, "");

const fmtCpf = (d: string): string => {
  if (d.length > 9)
    return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(
      9,
      11
    )}`;
  if (d.length > 6) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
  if (d.length > 3) return `${d.slice(0, 3)}.${d.slice(3)}`;
  return d;
};

const fmtCnpj = (d: string): string => {
  if (d.length > 12)
    return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(
      8,
      12
    )}-${d.slice(12, 14)}`;
  if (d.length > 8)
    return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8)}`;
  if (d.length > 5) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5)}`;
  if (d.length > 2) return `${d.slice(0, 2)}.${d.slice(2)}`;
  return d;
};

/** CPF 000.000.000-00 (≤11 díg.) ↔ CNPJ 00.000.000/0000-00 (>11 díg.). */
export const maskCpfCnpj = (v: string): string => {
  const d = onlyDigits(v).slice(0, 14);
  return d.length <= 11 ? fmtCpf(d) : fmtCnpj(d);
};

/** CEP 00000-000. */
export const maskCep = (v: string): string => {
  const d = onlyDigits(v).slice(0, 8);
  return d.length > 5 ? `${d.slice(0, 5)}-${d.slice(5)}` : d;
};

/** Telefone (00) 0000-0000 (≤10 díg.) ↔ (00) 00000-0000 (11 díg.). */
export const maskTelefone = (v: string): string => {
  const d = onlyDigits(v).slice(0, 11);
  if (d.length > 10) return d.replace(/^(\d{2})(\d{5})(\d{4})$/, "($1) $2-$3");
  if (d.length > 6) return d.replace(/^(\d{2})(\d{4})(\d{0,4})$/, "($1) $2-$3");
  if (d.length > 2) return d.replace(/^(\d{2})(\d{0,4})/, "($1) $2");
  return d;
};

export type QuestaoMetadata =
  | {
      inputMask?: string;
      maskType?: "cpfCnpj" | "phone" | string;
      acceptOnlyNumbers?: boolean;
      validation?: string;
      type?: string;
      options?: string[];
    }
  | null
  | undefined;

export const applyMaskFromMetadata = (
  meta: QuestaoMetadata,
  value: string
): string => {
  if (!meta) return value;
  if (meta.maskType === "cpfCnpj") return maskCpfCnpj(value);
  if (meta.maskType === "phone") return maskTelefone(value);
  if (meta.inputMask === "00000-000") return maskCep(value);
  return value;
};

export const hasMask = (meta: QuestaoMetadata): boolean =>
  !!meta &&
  (meta.maskType === "cpfCnpj" ||
    meta.maskType === "phone" ||
    meta.inputMask === "00000-000");

export const isUfField = (meta: QuestaoMetadata): boolean =>
  meta?.type === "uf";

export const isEmailField = (meta: QuestaoMetadata): boolean =>
  meta?.inputMask === "email" || meta?.validation === "email";

export const UF_OPTIONS: string[] = [
  "AC",
  "AL",
  "AP",
  "AM",
  "BA",
  "CE",
  "DF",
  "ES",
  "GO",
  "MA",
  "MT",
  "MS",
  "MG",
  "PA",
  "PB",
  "PR",
  "PE",
  "PI",
  "RJ",
  "RN",
  "RS",
  "RO",
  "RR",
  "SC",
  "SP",
  "SE",
  "TO"
];

export const ufSelectOptions = (
  meta?: QuestaoMetadata
): { value: string; label: string }[] =>
  (meta?.options && meta.options.length ? meta.options : UF_OPTIONS).map(
    (u) => ({ value: u, label: u })
  );

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const isValidEmail = (v?: string): boolean => !!v && EMAIL_RE.test(v);
